import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { formatDateOnly } from '../utils/timezone';

export default function ProcessMemberModal({ user: selectedUser, onClose, onSuccess }) {
  const { token, user: authUser } = useAuth();
  const { t, i18n } = useTranslation('app');
  const [processingType, setProcessingType] = useState('live');
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [deductionAmount, setDeductionAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [pendingOrder, setPendingOrder] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selections, setSelections] = useState({});
  const [selectedSeries, setSelectedSeries] = useState('Y');

  // Fetch packages on mount
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/packages`);
        if (!response.ok) throw new Error('Failed to fetch packages');
        const data = await response.json();
        setPackages(data.packages || []);
      } catch (err) {
        console.error('Error fetching packages:', err);
        setError('Failed to load packages');
      }
    };

    fetchPackages();
  }, []);

  // Check for pending order (from user data passed in)
  useEffect(() => {
    if (selectedUser && selectedUser.pending_order_id) {
      // User has a pending order
      setPendingOrder({
        id: selectedUser.pending_order_id,
        package_name: selectedUser.pending_order_package_name,
        package_price: selectedUser.pending_order_package_price,
        requested_at: selectedUser.pending_order_requested_at
      });
    }
  }, [selectedUser]);

  // Derive available series with stable ordering
  const SERIES_ORDER = ['Y', 'L', 'M'];
  const availableSeries = [...new Set(packages.map(p => p.series || 'Y'))]
    .sort((a, b) => {
      const ia = SERIES_ORDER.indexOf(a), ib = SERIES_ORDER.indexOf(b);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });

  // Series label lookup for future marketing flexibility
  const SERIES_LABELS = { Y: 'Y-Series', L: 'L-Series', M: 'M-Series' };

  // Clear selection if package list changes (e.g., packages finish loading)
  useEffect(() => {
    setSelectedPackageId('');
    setSelections({});
  }, [selectedSeries, packages]);

  // Fallback: if current series disappears, switch to first available
  useEffect(() => {
    if (availableSeries.length && !availableSeries.includes(selectedSeries)) {
      setSelectedSeries(availableSeries[0]);
    }
  }, [availableSeries, selectedSeries]);

  const hasAllSelectionsForPackage = (packageId) => {
    const pkg = packages.find(p => p.id === packageId);
    if (!pkg || !pkg.items || pkg.items.length === 0) return true;
    for (const item of pkg.items) {
      if (item.options.length > 1) {
        if (!selections[packageId]?.[item.slot_number]) return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[ProcessMemberModal] Form submitted', { processingType, selectedPackageId, userId: selectedUser?.id });
    setSubmitting(true);
    setError('');

    try {
      // Validate required fields based on processing type
      if (!selectedPackageId) {
        console.log('[ProcessMemberModal] Validation: no package selected');
        setError(t('processMember.selectPackageError'));
        setSubmitting(false);
        return;
      }

      // Validate all selections are made (not needed for pre_processed)
      if (processingType !== 'pre_processed' && !hasAllSelectionsForPackage(selectedPackageId)) {
        setError(t('processMember.completeSelectionsError'));
        setSubmitting(false);
        return;
      }

      if (processingType === 'backfilled') {
        if (!deductionAmount) {
          setError(t('processMember.deductionRequiredError'));
          setSubmitting(false);
          return;
        }
        // Validate deduction amount: must be > 0 and max 2 decimal places
        const amount = parseFloat(deductionAmount);
        if (isNaN(amount) || amount <= 0) {
          setError(t('processMember.deductionPositiveError'));
          setSubmitting(false);
          return;
        }
        if (!/^\d+(\.\d{1,2})?$/.test(deductionAmount)) {
          setError(t('processMember.deductionDecimalError'));
          setSubmitting(false);
          return;
        }
      }

      const selected_item_ids = selections[selectedPackageId] ? Object.values(selections[selectedPackageId]) : [];

      // Pre-Processed uses different endpoint and payload
      if (processingType === 'pre_processed') {
        const requestBody = {
          user_id: selectedUser.id,
          package_id: selectedPackageId,
          notes: notes || null
        };
        console.log('[ProcessMemberModal] Pre-Processing request:', requestBody);

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/pre-process-member`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestBody)
        });

        console.log('[ProcessMemberModal] Response status:', response.status);
        const data = await response.json();
        console.log('[ProcessMemberModal] Response data:', data);

        if (!response.ok) {
          let errorMessage = data.message || data.error || t('processMember.genericError');
          if (data.code === 'ALREADY_VERIFIED') {
            errorMessage = t('processMember.alreadyVerifiedError') || 'Member is already verified and cannot be pre-processed';
          }
          throw new Error(errorMessage);
        }

        // Success
        console.log('[ProcessMemberModal] Pre-Process success! Calling onSuccess and onClose');
        onSuccess();
        onClose();
      } else {
        // Regular process-member flow (live/open_slot/backfilled)
        const requestBody = {
          processing_type: processingType,
          user_id: selectedUser.id,
          package_id: selectedPackageId,
          deduction_amount: processingType === 'backfilled' ? parseFloat(deductionAmount) : null,
          notes: notes || null,
          selected_item_ids: selected_item_ids
        };
        console.log('[ProcessMemberModal] Sending request:', requestBody);

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/process-member`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestBody)
        });

        console.log('[ProcessMemberModal] Response status:', response.status);
        const data = await response.json();
        console.log('[ProcessMemberModal] Response data:', data);

        if (!response.ok) {
          let errorMessage = data.message || data.error || t('processMember.genericError');
          if (data.code === 'SELECTION_REQUIRED') {
            errorMessage = t('processMember.selectionRequiredError');
          } else if (data.code === 'INVALID_SELECTION') {
            errorMessage = t('processMember.invalidSelectionError');
          } else if (data.code === 'INVALID_PACKAGE_CONFIG') {
            errorMessage = t('processMember.invalidConfigError');
          }
          throw new Error(errorMessage);
        }

        // Success
        console.log('[ProcessMemberModal] Success! Calling onSuccess and onClose');
        onSuccess();
        onClose();
      }
    } catch (err) {
      console.error('Error processing member:', err);
      setError(err.message || t('processMember.genericError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!selectedUser) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return formatDateOnly(dateStr);
  };

  const isSuperadmin = authUser?.role === 'superadmin';
  const processingTypes = ['live', 'open_slot', 'backfilled', ...(isSuperadmin ? ['pre_processed', 'backfill_bonus'] : [])];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900">{t('processMember.title')}</h2>
          <p className="text-sm text-gray-600 mt-1">{selectedUser.first_name} {selectedUser.last_name}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Processing Type Selector */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">{t('processMember.processingType')}</label>
            <div className="flex gap-2 flex-wrap">
              {processingTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => {
                    setProcessingType(type);
                    setSelectedPackageId('');
                    setDeductionAmount('');
                    if (type === 'pre_processed') {
                      setSelectedSeries('Y');
                    }
                  }}
                  className={`flex-1 min-w-[80px] py-2 px-3 rounded font-medium text-sm transition ${
                    processingType === type
                      ? type === 'live'
                        ? 'bg-green-100 border-2 border-green-500 text-green-700'
                        : type === 'open_slot'
                        ? 'bg-gray-100 border-2 border-gray-500 text-gray-700'
                        : type === 'backfilled'
                        ? 'bg-blue-100 border-2 border-blue-500 text-blue-700'
                        : type === 'backfill_bonus'
                        ? 'bg-amber-100 border-2 border-amber-500 text-amber-800'
                        : 'bg-yellow-100 border-2 border-yellow-500 text-yellow-700'
                      : 'bg-gray-50 border-2 border-gray-200 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {type === 'live' && t('processMember.live')}
                  {type === 'open_slot' && t('processMember.openSlot')}
                  {type === 'backfilled' && t('processMember.backfilled')}
                  {type === 'pre_processed' && (t('processMember.preProcessed') || 'Pre-Processed')}
                  {type === 'backfill_bonus' && (i18n.language === 'zh' ? '🟠 回填单' : '🟠 Backfill Bonus')}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500">
              {processingType === 'live' && t('processMember.liveDesc')}
              {processingType === 'open_slot' && t('processMember.openSlotDesc')}
              {processingType === 'backfilled' && t('processMember.backfilledDesc')}
              {processingType === 'pre_processed' && (t('processMember.preProcessedDesc') || 'Grant Credit Points without completing a purchase')}
              {processingType === 'backfill_bonus' && (i18n.language === 'zh'
                ? '激活套餐，但不发放欢迎奖金。该账户未来赚取的直推、团队奖金、团队助推器、被动分红将用于抵扣套餐价格，直至抵扣金额达到套餐价格。'
                : "Activates the package with no welcome bonus. This account's future Direct Referral, Team Bonus, Team Booster, and Passive earnings will count toward paying off the package price until fully paid.")}
            </p>
          </div>

          {/* Pending Order Info (Live only) */}
          {processingType === 'live' && pendingOrder && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
              <p className="font-medium text-blue-900">{t('processMember.confirmingOrder')}</p>
              <p className="text-blue-800">{pendingOrder.package_name} — ${pendingOrder.package_price}</p>
              <p className="text-blue-600 text-xs">{t('processMember.submitted')} {formatDate(pendingOrder.requested_at)}</p>
            </div>
          )}

          {/* Package Selector (not for Live with pending order, shown for all other types including Pre-Processed) */}
          {!(processingType === 'live' && pendingOrder) && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">{t('processMember.package')}</label>

              {/* Series Tabs (hidden for Pre-Processed) */}
              {processingType !== 'pre_processed' && availableSeries.length > 1 && (
                <div className="flex gap-2 mb-3">
                  {availableSeries.map((series) => (
                    <button
                      key={series}
                      type="button"
                      onClick={() => setSelectedSeries(series)}
                      className={`py-2 px-4 rounded font-medium text-sm transition ${
                        selectedSeries === series
                          ? 'bg-blue-100 border-2 border-blue-500 text-blue-700'
                          : 'bg-gray-50 border-2 border-gray-200 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {SERIES_LABELS[series] || `${series}-Series`}
                    </button>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {packages
                  .filter((pkg) => {
                    if (processingType === 'pre_processed') {
                      // Only show 4 basic packages for Pre-Processed: 0550, 0880, 4500, 38000
                      const basicCodes = ['0550', '0880', '4500', '38000'];
                      return basicCodes.includes(pkg.code);
                    }
                    return (pkg.series || 'Y') === selectedSeries;
                  })
                  .reduce((seenById, pkg) => {
                    // For Pre-Processed and other types, keep all packages (one per ID)
                    if (!seenById.has(pkg.id)) {
                      seenById.set(pkg.id, pkg);
                    }
                    return seenById;
                  }, new Map())
                  .values()
                  .map((pkg) => (
                  <button
                    type="button"
                    key={pkg.id}
                    onClick={() => { setSelectedPackageId(pkg.id); setSelections({}); }}
                    className={`text-left border-2 rounded-md overflow-hidden transition-colors ${
                      selectedPackageId === pkg.id
                        ? 'border-blue-500 ring-2 ring-blue-200'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="w-full aspect-square bg-gray-100 flex items-center justify-center">
                      {pkg.image_url ? (
                        <img src={pkg.image_url} alt={pkg.code} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-gray-400 text-xs font-mono">{pkg.code}</span>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-xs font-mono text-gray-500">{pkg.code}</p>
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {i18n.language === 'zh' ? pkg.name_cn : pkg.name_en}
                      </p>
                      <p className="text-sm text-gray-600">{pkg.price} {t('processMember.creditPoints')}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Package Items (What's Included) — not shown for Pre-Processed */}
          {processingType !== 'pre_processed' && selectedPackageId && packages.find(p => p.id === selectedPackageId)?.items && (
            <div className="space-y-3 border border-gray-200 rounded-md p-4 bg-gray-50">
              <h3 className="text-sm font-medium text-gray-700">{t('processMember.whatsIncluded')}</h3>
              <div className="space-y-3">
                {packages.find(p => p.id === selectedPackageId).items.map((slot) => (
                  <div key={slot.slot_number}>
                    {slot.options.length === 1 ? (
                      <div className="flex items-start gap-2 text-sm">
                        <span className="text-gray-400 flex-shrink-0">•</span>
                        <span className="text-gray-700">
                          {i18n.language === 'zh' ? slot.options[0].option_label_cn : slot.options[0].option_label_en}
                        </span>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-700">{t('processMember.chooseOne')}</p>
                        <div className="space-y-2 pl-4">
                          {slot.options.map((option) => (
                            <label key={option.id} className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="radio"
                                name={`slot_${selectedPackageId}_${slot.slot_number}`}
                                value={option.id}
                                checked={selections[selectedPackageId]?.[slot.slot_number] === option.id}
                                onChange={() => {
                                  setSelections(prev => ({
                                    ...prev,
                                    [selectedPackageId]: {
                                      ...prev[selectedPackageId],
                                      [slot.slot_number]: option.id
                                    }
                                  }));
                                }}
                              />
                              <span className="text-sm text-gray-700">
                                {i18n.language === 'zh' ? option.option_label_cn : option.option_label_en}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Deduction Amount (Backfilled only) */}
          {processingType === 'backfilled' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">{t('processMember.deductionAmount')}</label>
              <input
                type="number"
                step="0.01"
                value={deductionAmount}
                onChange={(e) => setDeductionAmount(e.target.value)}
                placeholder="e.g., 550.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-gray-500">{t('processMember.deductionHelp')}</p>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">{t('processMember.notes')}</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('processMember.notesPlaceholder')}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition"
              disabled={submitting}
            >
              {t('processMember.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting || (!selectedPackageId) || (processingType !== 'pre_processed' && selectedPackageId && !hasAllSelectionsForPackage(selectedPackageId))}
              title={!selectedPackageId ? t('processMember.selectPackageError') : (processingType !== 'pre_processed' && selectedPackageId && !hasAllSelectionsForPackage(selectedPackageId) ? t('processMember.completeSelectionsFirst') : '')}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? t('processMember.processing') : (processingType === 'pre_processed' ? (t('processMember.preProcessButton') || 'Pre-Process Member') : t('processMember.processMemberButton'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
